import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { StagingTestUserPicker } from '../components/StagingTestUserPicker';
import { useLocalBankIdDebug } from '../context/LocalBankIdDebugContext';
import { adminApi } from '../services/api';

export function AdminTestUsers() {
  const { t } = useTranslation();
  const { retry: refreshTestActors } = useLocalBankIdDebug();
  const [ids, setIds] = useState<string[]>([]);
  const [configurable, setConfigurable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setMessage(null);
    adminApi.getDebugSettings().then(settings => {
      if (!active) return;
      setIds(settings.bankidStagingTestUserIds ?? []);
      setConfigurable(settings.bankidStagingTestUsersConfigurable === true);
    }).catch(() => {
      if (active) {
        setConfigurable(false);
        setMessage({ type: 'error', text: t('adminDebugSettings.stagingTesters.loadError') });
      }
    }).finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, [t, attempt]);

  const save = async () => {
    setIsSaving(true);
    setMessage(null);
    try {
      const settings = await adminApi.updateStagingTestUsers(ids);
      setIds(settings.bankidStagingTestUserIds ?? []);
      setConfigurable(settings.bankidStagingTestUsersConfigurable === true);
      refreshTestActors();
      setMessage({ type: 'success', text: t('adminDebugSettings.stagingTesters.saved') });
    } catch {
      setMessage({ type: 'error', text: t('adminDebugSettings.stagingTesters.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  return <DashboardLayout>
    <Breadcrumb items={[
      { label: t('adminDebugSettings.breadcrumb.system'), path: '/admin/system/contract-config' },
      { label: t('adminDebugSettings.stagingTesters.title') }
    ]} />
    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 mb-4">
      <h4 className="mb-0">{t('adminDebugSettings.stagingTesters.title')}</h4>
      <Button size="lg" onClick={save} disabled={isLoading || isSaving || !configurable}>
        {t(isSaving ? 'adminDebugSettings.actions.saving' : 'adminDebugSettings.actions.save')}
      </Button>
    </div>
    {message && <div role={message.type === 'error' ? 'alert' : 'status'} className={`alert alert-${message.type === 'success' ? 'success' : 'danger'}`}>
      {message.text}
      {message.type === 'error' && !configurable && <Button variant="secondary" size="lg" className="d-block mt-3" onClick={() => setAttempt(value => value + 1)}>
        {t('localBankIdDebug.retry')}
      </Button>}
    </div>}
    {isLoading ? <p role="status">{t('common.loading')}</p> : <Card>
      <StagingTestUserPicker value={ids} onChange={value => { setIds(value); setMessage(null); }} disabled={!configurable} readOnly={isSaving} />
    </Card>}
  </DashboardLayout>;
}
